

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { API_URL } from "./config";

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);

  const getToken = () => {
    const tokenString = localStorage.getItem("authToken");
    if (!tokenString) return null;
    try {
      return JSON.parse(tokenString);
    } catch {
      return null;
    }
  };

  const fetchNotifications = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    try {
      setLoading(true);

      const res = await fetch(`${API_URL}/notifications/`, {
        headers: {
          Authorization: `Bearer ${token.access}`,
        },
      });

      if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);

      const data = await res.json();

      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (err) {
      console.error("fetchNotifications error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const markAllAsRead = async () => {
    if (unreadCount === 0 || markingRead) return;

    const token = getToken();
    if (!token) return;

    // ── Optimistic update ──
    const prevNotifications = notifications;
    const prevUnreadCount = unreadCount;

    setUnreadCount(0);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));

    try {
      setMarkingRead(true);

    const res = await fetch(`${API_URL}/notifications/mark-all-read/`, {
      method: "PATCH", // ← was "POST", backend returns 405 for POST
      headers: {
        Authorization: `Bearer ${token.access}`,
        "Content-Type": "application/json",
      },
    });

      if (!res.ok) {
        // ── Rollback on failure ──
        console.error("mark-all-read failed:", res.status, await res.text());
        setNotifications(prevNotifications);
        setUnreadCount(prevUnreadCount);
        return;
      }

      // ── Re-sync from server to confirm ──
      await fetchNotifications();
    } catch (err) {
      // ── Rollback on network error ──
      console.error("markAllAsRead error:", err);
      setNotifications(prevNotifications);
      setUnreadCount(prevUnreadCount);
    } finally {
      setMarkingRead(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        markingRead,
        fetchNotifications,
        markAllAsRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => useContext(NotificationContext);
